/// <amd-module name="Feed/Legacy/Actions/FeedSettings/SavePermissionsHandler" />
import { IVssPageContext } from "VSS/Platform/Context";
import { Identity } from "VSS/Platform/Contracts/Identities";
import { announce } from "VSS/Core/Util/Accessibility";

import { IFeedSettingsState } from "Feed/Legacy/Components/Settings/IFeedSettingsState";
import { IFeedDataService } from "Feed/Legacy/DataServices/FeedsDataService";
import { IdentityDataService } from "Feed/Legacy/DataServices/IdentityDataService";
import * as PackageResources from "Feed/Common/Resources";
import { FeedPermission } from "Feed/Common/Contracts/Feed";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

/**
 * User clicked 'Save' button in 'Add users or groups' panel
 * save new users/groups to server and add to grid
 */
export class SavePermissionsHandler {
    public static async handleAsync(state: IFeedSettingsState, permissionsToAdd: FeedPermission[]): Promise<void> {
        if (permissionsToAdd == null || permissionsToAdd.length === 0) {
            // shouldn't encounter this case, if you do then you're calling this method in wrong place
            throw new Error("permissionsToAdd cannot be null or empty");
        }

        await this.setIdentityDescriptorsAsync(state.pageContext, permissionsToAdd);

        await this.setFeedPermissionsAsync(state.pageContext, state.feed(), permissionsToAdd);

        // ideally we should just set state.feed.permissions with permissions but server
        // doesn't return all properties only identityDescriptor and role
        state.feedPermissions = state.feedPermissions.concat(permissionsToAdd);

        announce(PackageResources.SavedAnnouncement);
    }

    public static async setIdentityDescriptorsAsync(
        pageContext: IVssPageContext,
        permissionsToAdd: FeedPermission[]
    ): Promise<void> {
        const identityIds = [];
        permissionsToAdd.forEach((permission: FeedPermission) => {
            identityIds.push(permission.identityId);
        });

        // need to get descriptors for recently added user/group because IdentityPicker
        // doesn't return descriptors
        const identityDataService = pageContext.getService<IdentityDataService>("IFeedIdentityDataService");
        const identities: Identity[] = await identityDataService.readIdentitiesAsync(identityIds);

        identities.forEach((identity: Identity, index: number) => {
            permissionsToAdd[index].identityDescriptor = identity.descriptor;
        });
    }

    private static async setFeedPermissionsAsync(
        pageContext: IVssPageContext,
        feed: Feed,
        permissionsToUpdate: FeedPermission[]
    ): Promise<FeedPermission[]> {
        const feedDataService = pageContext.getService<IFeedDataService>("IFeedDataService");
        return feedDataService.setFeedPermissionsAsync(feed, permissionsToUpdate);
    }
}
